import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Bell } from 'lucide-react';

function priorityStyle(priority) {
  if (priority === 'high') return 'border-red-300 text-red-600';
  if (priority === 'medium') return 'border-amber-300 text-amber-600';
  return 'border-slate-300 text-slate-500';
}

export function NoticesSection({ notices, setSelectedNotice }) {
  return (
    <Card className="shadow-lg border-0">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="w-5 h-5" />
          Management Posts
        </CardTitle>
      </CardHeader>
      <CardContent>
        {notices.length === 0 ? (
          <p className="text-center text-slate-500 py-8">No management posts</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {notices.map((notice) => (
              <div
                key={notice.id}
                data-testid={`notice-card-${notice.id}`}
                className="p-4 bg-slate-50 rounded-lg border border-slate-200 cursor-pointer hover:border-gk-teal/40 hover:bg-gk-bg/50 transition-all h-[120px] flex flex-col justify-between"
                onClick={() => setSelectedNotice(notice)}
              >
                <div className="flex-1 min-h-0">
                  <h4 className="font-semibold text-sm leading-snug line-clamp-1">{notice.title}</h4>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{notice.content}</p>
                </div>
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-200">
                  <p className="text-[11px] text-slate-400 truncate max-w-[60%]">By {notice.author_name}</p>
                  <div className="flex items-center gap-1.5">
                    {notice.expires_at && (
                      <span className="text-[10px] text-slate-400">Expires: {notice.expires_at}</span>
                    )}
                    <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${priorityStyle(notice.priority)}`}>{notice.priority}</Badge>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function NoticeModals({
  showNoticesModal,
  setShowNoticesModal,
  notices,
  selectedNotice,
  setSelectedNotice
}) {
  return (
    <>
      <Dialog open={showNoticesModal} onOpenChange={setShowNoticesModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-600" />
              All Management Posts ({notices.length})
            </DialogTitle>
            <DialogDescription>Complete list of management posts</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {notices.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No management posts</p>
            ) : (
              notices.map((notice) => (
                <div
                  key={notice.id}
                  data-testid={`notice-list-${notice.id}`}
                  className="p-4 bg-slate-50 rounded-lg border-l-4 border-gk-teal cursor-pointer hover:bg-gk-bg transition-colors"
                  onClick={() => { setSelectedNotice(notice); setShowNoticesModal(false); }}
                >
                  <div className="flex items-start justify-between mb-1">
                    <h4 className="font-semibold text-sm">{notice.title}</h4>
                    <Badge variant="outline" className={`text-xs ${priorityStyle(notice.priority)}`}>{notice.priority}</Badge>
                  </div>
                  <p className="text-xs text-slate-500 line-clamp-1">{notice.content}</p>
                  <p className="text-[11px] text-slate-400 mt-1">By {notice.author_name}</p>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedNotice} onOpenChange={(open) => { if (!open) setSelectedNotice(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedNotice?.title}</DialogTitle>
            <DialogDescription>Posted by {selectedNotice?.author_name}</DialogDescription>
          </DialogHeader>
          {selectedNotice && (
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={`${priorityStyle(selectedNotice.priority)}`}>{selectedNotice.priority} priority</Badge>
                <span className="text-xs text-slate-400">{new Date(selectedNotice.created_at).toLocaleString()}</span>
                {selectedNotice.expires_at && (
                  <span className="text-xs text-amber-600 font-medium">Expires: {selectedNotice.expires_at}</span>
                )}
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedNotice.content}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
